import { Button, TableColumnsType, Tag } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';

type RT = Dashboard.ReceptionScore.AssistantDetail;

export interface AssistantDetailTableProps extends ScrollTableProps<RT> {
  onCheckDetail: (record: RT) => void;
}

export const useReplyOptionElementMap = () => {
  const { t } = useTranslate();

  const replyOptionElementMap = {
    '1': <Tag color="green">{t('aide.好评')}</Tag>,
    '2': <Tag color="red">{t('aide.差评')}</Tag>,
  };

  return {
    t,
    replyOptionElementMap,
  };
};

export const useBuyerTypeLabelMap = () => {
  const { t } = useTranslate();

  const buyerTypeLabelMap = {
    PRE_SALE: t('aide.售前买家'),
    AFTER_SALE: t('aide.售后买家'),
  };

  return {
    t,
    buyerTypeLabelMap,
  };
};

function AssistantDetailTable(props: AssistantDetailTableProps) {
  const { onCheckDetail, ...otherProps } = props;

  const { t } = useReplyOptionElementMap();

  const { buyerTypeLabelMap } = useBuyerTypeLabelMap();

  const columns: TableColumnsType<RT> = [
    {
      key: 'rawReplyOption',
      dataIndex: 'rawReplyOption',
      title: t('receptionScore.rawReply', '买家回复数字'),
      render: (value) => value || '—',
    },
    {
      key: 'labelName',
      dataIndex: 'labelName',
      title: t('receptionScore.labelName', '评价标签'),
      render: (value) => value || '—',
    },
    {
      key: 'sendMode',
      dataIndex: 'sendMode',
      title: t('receptionScore.sendMode', '邀评来源'),
      render: (value) =>
        value === 'MANUAL'
          ? t('receptionScore.manual', '手动')
          : value === 'AUTO'
          ? t('receptionScore.auto', '自动')
          : t('receptionScore.legacy', '历史未记录'),
    },
    {
      key: 'operatorId',
      dataIndex: 'operatorId',
      title: t('receptionScore.operator', '邀评操作人 ID'),
      render: (value) => value || '—',
    },
    {
      key: 'id',
      dataIndex: 'id',
      title: 'ID',
      fixed: 'left',
      width: 60,
    },
    {
      key: 'storeName',
      dataIndex: 'storeName',
      title: t('marketing.店铺名称'),
      width: 150,
    },
    {
      key: 'assistantName',
      dataIndex: 'assistantName',
      title: t('aide.接待客服'),
      width: 150,
    },
    {
      key: 'replyOption',
      dataIndex: 'replyOption',
      title: t('aide.评分结果'),
      width: 150,
      render: (_, record) => <ScoreResult record={record} />,
    },
    {
      key: 'buyerNick',
      dataIndex: 'buyerNick',
      title: t('marketing.买家昵称'),
      width: 150,
    },
    {
      key: 'buyerType',
      dataIndex: 'buyerType',
      title: t('aide.买家类型'),
      width: 120,
      render: (buyerType: RT['buyerType']) => {
        return buyerTypeLabelMap[buyerType];
      },
    },
    {
      key: 'inviteTime',
      dataIndex: 'inviteTime',
      title: t('aide.邀请评分时间'),
      width: 200,
      render: (inviteTime: RT['inviteTime']) => dayjs(inviteTime).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      key: 'replyTime',
      dataIndex: 'replyTime',
      title: t('aide.买家评分时间'),
      width: 200,
      render: (replyTime: RT['replyTime']) => dayjs(replyTime).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      key: 'operation',
      title: t('操作'),
      fixed: 'right',
      width: 120,
      render: (_, record) => {
        return (
          <Button type="link" onClick={() => onCheckDetail(record)} style={{ paddingLeft: 0 }}>
            {t('aide.查看接待明细')}
          </Button>
        );
      },
    },
  ];

  return <ScrollTable rowKey="id" columns={columns} {...otherProps} />;
}

export default AssistantDetailTable;

export function ScoreResult({
  record,
}: {
  record: { scoreType?: string; replyOption?: string };
}) {
  const { t } = useTranslate();
  const score = record.scoreType;
  return score === 'POSITIVE' ? (
    <Tag color="green">{t('aide.好评')}</Tag>
  ) : score === 'NEGATIVE' ? (
    <Tag color="red">{t('aide.差评')}</Tag>
  ) : (
    <span>—</span>
  );
}
